import { useCallback, useEffect, useState } from 'react';
import { emailKanalAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Mail, Plus, Save, ShieldCheck, ShieldAlert, ShieldQuestion, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

/* E-Mail-Kanal (Erweiterung v2, Modul A) — Einstellungen → Technik.
   Drei Blöcke: Domain-Status (SPF/DKIM/DMARC), Vorlagen, Versandprotokoll.
   Die Zugangsdaten des Versandwegs liegen unter Integrationen — hier geht es
   um den Kanal selbst. */

const MANDANTEN = ['p1', 'ptl', 'zinzino'];
const LEER = { slug: '', mandant: 'p1', typ: 'transaktional', betreff: '', text: '' };

function DomainZeile({ name, status }) {
  const Icon = status?.ok === true ? ShieldCheck : status?.ok === false ? ShieldAlert : ShieldQuestion;
  const farbe = status?.ok === true ? 'text-green-600' : status?.ok === false ? 'text-destructive' : 'text-muted-foreground';
  return (
    <div className="flex items-start gap-2 text-sm">
      <Icon className={`h-4 w-4 mt-0.5 flex-none ${farbe}`} />
      <div className="min-w-0">
        <span className="font-medium">{name}</span>{' '}
        <span className="text-muted-foreground break-all">
          {status?.eintrag || status?.hinweis || (status?.ok === false ? 'kein Eintrag gefunden' : '')}
        </span>
      </div>
    </div>
  );
}

export default function EmailKanalTab({ onZumVersandweg }) {
  const [domain, setDomain] = useState(null);
  const [vorlagen, setVorlagen] = useState([]);
  const [log, setLog] = useState([]);
  const [editor, setEditor] = useState(null);
  const [lade, setLade] = useState(true);

  const laden = useCallback(async () => {
    setLade(true);
    try {
      const [d, v, n] = await Promise.all([
        emailKanalAPI.domainStatus(), emailKanalAPI.vorlagen(), emailKanalAPI.nachrichten(),
      ]);
      setDomain(d.data); setVorlagen(v.data); setLog(n.data);
    } catch { toast.error('E-Mail-Kanal konnte nicht geladen werden'); }
    setLade(false);
  }, []);
  useEffect(() => { laden(); }, [laden]);

  const speichern = async () => {
    try {
      await emailKanalAPI.vorlageSchreiben(editor);
      toast.success(`Vorlage „${editor.slug}" gespeichert`);
      setEditor(null); laden();
    } catch (e) { toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || 'Speichern fehlgeschlagen'); }
  };

  if (lade) return <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-4" data-testid="email-kanal-tab">
      {/* Versandweg liegt unter Integrationen → Email; hier der Wegweiser (24.09.2026) */}
      <Card className="border-marke/30 bg-red-50/30 dark:bg-red-950/10">
        <CardContent className="p-4 flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <p className="text-sm font-semibold">Versandweg: Postfach (IONOS) oder Brevo</p>
            <p className="text-xs text-muted-foreground">Zugangsdaten, Brevo-API-Schlüssel, Webhook-Token und der Umschalter liegen unter Integrationen → Reiter Email → Zeile „Versandweg".</p>
          </div>
          {onZumVersandweg && <Button size="sm" onClick={onZumVersandweg} data-testid="zum-versandweg">Zum Versandweg</Button>}
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Absenderdomain {domain?.domain ? `(${domain.domain})` : ''}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {domain?.domain ? (
            <>
              <DomainZeile name="SPF" status={domain.spf} />
              <DomainZeile name="DMARC" status={domain.dmarc} />
              <DomainZeile name="DKIM" status={domain.dkim} />
              <p className="text-xs text-muted-foreground pt-1">
                Fehlende Einträge legt dein Domain-Anbieter (DNS) an — ohne SPF und DKIM landen
                Mails deutlich öfter im Spam. Den DKIM-Selector trägst du unter Integrationen →
                E-Mail ein, sobald der Versandanbieter ihn nennt.
              </p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">{domain?.hinweis || 'Keine Absenderadresse konfiguriert — zuerst unter Integrationen → E-Mail einrichten.'}</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Vorlagen</CardTitle>
          <Button size="sm" variant="outline" className="gap-1" onClick={() => setEditor({ ...LEER })}>
            <Plus className="h-4 w-4" /> Neue Vorlage
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Platzhalter wie <code className="bg-muted px-1 rounded">{'{{vorname}}'}</code> werden beim
            Versand ersetzt. Marketing-Vorlagen gehen technisch nur an Empfänger mit bestätigtem
            Double-Opt-in und tragen automatisch den Abmeldelink.
          </p>
          {vorlagen.length === 0 && !editor && (
            <p className="text-sm text-muted-foreground">Noch keine Vorlagen — Bestätigungen nutzen bis dahin die eingebauten Texte.</p>
          )}
          {vorlagen.map((v) => (
            <div key={`${v.mandant}:${v.slug}`}
                 className="flex items-center justify-between gap-2 border border-border rounded-md px-3 py-2 text-sm">
              <div className="min-w-0">
                <span className="font-medium">{v.slug}</span>
                <span className="text-muted-foreground"> · {v.betreff}</span>
              </div>
              <div className="flex items-center gap-2 flex-none">
                <Badge variant="outline">{v.mandant.toUpperCase()}</Badge>
                <Badge variant={v.typ === 'marketing' ? 'destructive' : 'secondary'}>{v.typ}</Badge>
                <Button size="sm" variant="ghost" onClick={() => setEditor({ ...LEER, ...v })}>Bearbeiten</Button>
              </div>
            </div>
          ))}

          {editor && (
            <div className="border border-primary/40 rounded-md p-3 space-y-2">
              <div className="grid md:grid-cols-3 gap-2">
                <input placeholder="slug (z. B. seminar-bestaetigung)" value={editor.slug}
                       onChange={(e) => setEditor({ ...editor, slug: e.target.value })}
                       className="text-sm rounded-md border border-border bg-background px-2 py-1.5" />
                <select value={editor.mandant} onChange={(e) => setEditor({ ...editor, mandant: e.target.value })}
                        className="text-sm rounded-md border border-border bg-background px-2 py-1.5">
                  {MANDANTEN.map((m) => <option key={m} value={m}>{m.toUpperCase()}</option>)}
                </select>
                <select value={editor.typ} onChange={(e) => setEditor({ ...editor, typ: e.target.value })}
                        className="text-sm rounded-md border border-border bg-background px-2 py-1.5">
                  <option value="transaktional">transaktional</option>
                  <option value="marketing">marketing</option>
                </select>
              </div>
              <input placeholder="Betreff" value={editor.betreff}
                     onChange={(e) => setEditor({ ...editor, betreff: e.target.value })}
                     className="w-full text-sm rounded-md border border-border bg-background px-2 py-1.5" />
              <textarea placeholder="Text — Platzhalter: {{vorname}}, {{termin}}, …" rows={6} value={editor.text}
                        onChange={(e) => setEditor({ ...editor, text: e.target.value })}
                        className="w-full text-sm rounded-md border border-border bg-background px-2 py-1.5 font-mono" />
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="ghost" onClick={() => setEditor(null)}>Abbrechen</Button>
                <Button size="sm" className="gap-1" onClick={speichern}
                        disabled={!editor.slug || !editor.betreff || !editor.text}>
                  <Save className="h-4 w-4" /> Speichern
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><Mail className="h-4 w-4" /> Letzte Sendungen</CardTitle>
        </CardHeader>
        <CardContent>
          {log.length === 0 ? (
            <p className="text-sm text-muted-foreground">Noch keine Sendungen protokolliert.</p>
          ) : (
            <table className="w-full text-sm">
              <tbody>
                {log.slice(0, 20).map((n) => (
                  <tr key={n.id} className="border-b border-border/40">
                    <td className="py-1 pr-2 whitespace-nowrap text-muted-foreground">
                      {new Date(n.erstellt_am).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td className="py-1 pr-2 truncate max-w-[16rem]">{n.an}</td>
                    <td className="py-1 pr-2 truncate max-w-[18rem] text-muted-foreground">{n.betreff}</td>
                    <td className="py-1 text-right">
                      <Badge variant={n.status === 'gesendet' ? 'secondary' : 'destructive'}
                             title={n.grund || ''}>
                        {n.status}{n.grund ? ` · ${n.grund}` : ''}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
